import React, { useState, useEffect, useRef } from 'react';
import './style.css';
import { removeOfflineData, isEffectivelyOffline } from '../../utils/helper';
import { useParams } from 'react-router-dom';
import { padServices } from '../../api';
import { useDispatch, useSelector } from 'react-redux';
import { setIsConflictModalVisible, setServerData } from '../../app/EditorSlice';

const OfflineStatus = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const isManualOffline = useSelector((state) => state.editor.isManualOffline);
  const [offline, setOffline] = useState(isEffectivelyOffline());
  const [isSyncing, setIsSyncing] = useState(false);
  const debounceTimer = useRef(null);

  /** Get offline data for the given ID */
  const getOfflineData = (id) => {
    const offlineData = JSON.parse(localStorage.getItem("offlineMilepadData") || "{}");
    return offlineData[id] || null;
  };

  /** Try syncing local data with server */
  const syncWithServer = async (id, localContent, localUpdateTime) => {
    const serverResponse = await padServices.fetchById(id);
    const serverUpdateTime = serverResponse?.data?.updatedAt
      ? new Date(serverResponse.data.updatedAt)
      : null;

    if (serverResponse?.data) {
      dispatch(setServerData(serverResponse.data));
    }

    // Conflict check
    if (
      localContent?.milepadData &&
      serverUpdateTime &&
      localUpdateTime &&
      serverUpdateTime > localUpdateTime
    ) {
      dispatch(setIsConflictModalVisible(true));
      return;
    }

    // Auto-sync if no conflict
    await padServices.saveToDatabase(
      id,
      localContent.milepadData,
      localContent.summary,
      dispatch
    );
    removeOfflineData(id);
  };

  /** Main handler for offline/online status */
  const handleOfflineStatus = async () => {
    clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(async () => {
      if (isSyncing) return;
      setIsSyncing(true);

      try {
        const stillOffline = isEffectivelyOffline();
        setOffline(stillOffline);

        if (stillOffline) return;

        const offlineEntry = getOfflineData(id);
        if (offlineEntry?.data) {
          const localUpdateTime = offlineEntry.lastUpdateTime
            ? new Date(offlineEntry.lastUpdateTime)
            : null;

          await syncWithServer(id, offlineEntry.data, localUpdateTime);
        }
      } catch (err) {
        console.error("Error during offline sync:", err);
      } finally {
        setIsSyncing(false);
      }
    }, 400);
  };

  useEffect(() => {
    // Initial check
    handleOfflineStatus();

    window.addEventListener("offline", handleOfflineStatus);
    window.addEventListener("online", handleOfflineStatus);

    return () => {
      window.removeEventListener("offline", handleOfflineStatus);
      window.removeEventListener("online", handleOfflineStatus);
      clearTimeout(debounceTimer.current);
    };
  }, [id, isManualOffline]);

  if (!offline) return null;

  return (
    <div className="offlineStatus">
      <div>
        {isManualOffline
          ? "You're working offline — changes are saved locally and we’ll sync up when you go back online."
          : "No internet? No problem! You're working offline — we’ll sync up when you're reconnected."}
      </div>
    </div>
  );
};

export default OfflineStatus;